import React, { useMemo } from 'react'
import { CmsPage } from '@pylot-data/fwrdschema'
import { useStoreConfig } from 'config'
import { getFirstNotEmpty } from '@corratech/pylot-utils'
import { PAGE_TYPE } from '../enums'
import { Organization } from './Organization'
import { SearchBox } from './SearchBox'
import { CommonMetaTags } from '../CommonMetaTags'
import { getTrimmedUrl } from '../util'

const HomeFBImage = 'home_facebook_image.jpg'
const HomeTwitterImage = 'home_twitter_image.jpg'
const CmsFBImage = 'cms_facebook_image.jpg'
const CmsTwitterImage = 'cms_twitter_image.jpg'

type Page = PAGE_TYPE
export type CMSPageProps = {
    cmsPage: CmsPage
    canonicalUrl: string
    titleOverride?: string
    descriptionOverride?: string
    page?: Page
    asPath: string
}

export const CMSPage = ({
    cmsPage,
    titleOverride,
    descriptionOverride,
    canonicalUrl,
    page,
    asPath
}: CMSPageProps): JSX.Element => {
    const config = useStoreConfig()
    const { seo, base } = config

    const mediaUrl = base.url.mediaBackend?.baseMediaUrl
    const cmsMediaUrl = `${mediaUrl}${base.url.media.cms}`
    const homeMediaUrl = `${mediaUrl}${base.url.media.home}`

    const imageContent: HTMLImageElement | null = useMemo(
        () =>
            (typeof document !== 'undefined' &&
                document.querySelector(
                    `img[src="${
                        page === PAGE_TYPE.HOME
                            ? homeMediaUrl.concat(HomeFBImage)
                            : cmsMediaUrl.concat(CmsFBImage)
                    }"]`
                )) ||
            null,
        [`${homeMediaUrl}${HomeFBImage}`]
    )

    const twitterImageContent =
        page === PAGE_TYPE.HOME
            ? `${homeMediaUrl}${HomeTwitterImage}`
            : `${cmsMediaUrl}${CmsTwitterImage}`
    const title = getFirstNotEmpty([
        titleOverride,
        cmsPage.meta_title,
        cmsPage.title,
        seo.default.title
    ])

    const description = getFirstNotEmpty([
        descriptionOverride,
        cmsPage?.meta_description,
        seo.default.description
    ])

    const generalData = {
        title: title,
        keyword: String(cmsPage.meta_keywords),
        description: String(description),
        canonicalUrl
    }

    const twitterData = {
        title: title,
        description: description,
        domain: seo.default.twitter?.handle,
        url: canonicalUrl,
        card: seo.default.twitter?.cardType,
        image: getTrimmedUrl(twitterImageContent),
        site: seo.default.twitter?.site,
        creator: seo.default.twitter?.creator
    }

    const ogData = {
        title: title,
        description: description,
        url: canonicalUrl,
        siteName: seo.default.openGraph?.site_name,
        image: getTrimmedUrl(imageContent?.src || ''),
        imageAlt: imageContent?.alt,
        imageWidth: imageContent?.width,
        imageHeight: imageContent?.height
    }

    return (
        <>
            <CommonMetaTags
                generalData={generalData}
                twitterData={twitterData}
                ogData={ogData}
            />
            {page === PAGE_TYPE.HOME && (
                <>
                    <Organization asPath={asPath} />
                    <SearchBox asPath={asPath} />
                </>
            )}
        </>
    )
}
